import { Modal } from "react-bootstrap";
import { useEffect } from "react";
import { confetti } from "tsparticles-confetti";
import useFlow from "../hooks/useFlow";
import { clearHubSpotState } from "../utils/hubspot";
import ClientTags from "./ClientTags";

/** Long enough to read the room name and glance at the map, short enough that
 * the next client does not walk up to it. */
const AUTO_CLOSE_MS = 10_000;

export default function MapResourceModal() {
  const {
    showMapResourceModal,
    setShowMapResourceModal,
    resource,
    locationPreferences,
  } = useFlow();

  const handleClose = () => {
    setShowMapResourceModal(false);
    clearHubSpotState();
  };

  /**
   * Close on its own after ten seconds.
   *
   * Nobody presses "Done" on the way to a yoga room, and this modal is the
   * last screen of a check-in, so without a timer it stays up until something
   * else clears it. Layout's idle reload does cover it — `showMapResourceModal`
   * is one of the conditions that arms it — but that takes 30s and reloads the
   * whole app, and it takes longer still to notice that the person standing at
   * the kiosk is the *next* client looking at the previous one's room map.
   *
   * Ten seconds is enough to read "head to MAHA" and see the map, and it
   * routes through the same `handleClose` the button uses, so the HubSpot
   * cleanup happens either way.
   */
  useEffect(() => {
    if (!showMapResourceModal) return;

    const timer = setTimeout(() => {
      setShowMapResourceModal(false);
      clearHubSpotState();
    }, AUTO_CLOSE_MS);

    return () => clearTimeout(timer);
  }, [showMapResourceModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const findResourceImage = (resource) => {
    if (!resource || !locationPreferences.roomMaps) return null;

    const found = locationPreferences.roomMaps.find(
      (map) => map.id === resource.id
    );
    return found ? found.src : null;
  };

  // Validation always disabled for now
  const isConfettiDisabled = true;

  useEffect(() => {
    if (!showMapResourceModal || isConfettiDisabled) return;

    const duration = 1000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 90,
      gravity: 1,
      decay: 0.94,
      zIndex: 2147483647,
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = Math.round(80 * (timeLeft / duration));
      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random() * 0.3 + 0.1, y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: Math.random() * 0.3 + 0.6, y: Math.random() - 0.2 },
      });
    }, 200);

    return () => clearInterval(interval);
  }, [showMapResourceModal, isConfettiDisabled]);

  return (
    <>
      {showMapResourceModal && resource?.tags && (
        <ClientTags tags={resource.tags} backdrop />
      )}
      <Modal
        show={showMapResourceModal}
        onHide={handleClose}
        centered
        size="lg"
      >
        {resource && (
          <Modal.Body>
            <div className="modal-header border-0 m-0 p-3 mt-2 pt-lg-0 pt-4">
              <h4 className="text-center w-100 pb-0 mb-0 mt-4 pt-4 pt-lg-0">
                You&apos;re in!
              </h4>
            </div>

            <div className="modal-body pl-4 pr-4">
              {resource.name && (
                <p className="text-center p-0 pb-2">
                  You can place your things in the cubbies
                  <br /> and head to{" "}
                  <strong className="classRoomName text-uppercase">
                    {resource.name}
                  </strong>
                </p>
              )}

              {findResourceImage(resource) && (
                <div className="class-map text-center">
                  <img
                    className="img-fluid"
                    style={{ maxHeight: 600 }}
                    src={findResourceImage(resource)}
                  />
                </div>
              )}
            </div>

            <div className="modal-footer border-0 text-center pt-4 mt-2 mb-4">
              <a
                className="btn btn-primary btn-lg m-auto done-btn"
                data-testid="done-btn"
                onClick={handleClose}
              >
                Done
              </a>
            </div>
          </Modal.Body>
        )}
      </Modal>
    </>
  );
}
